// Maps IANA timezones to ISO 3166-1 alpha-2 country codes used by Nager.Date API
export const TIMEZONE_TO_COUNTRY = {
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US',
  'Europe/London': 'GB',
  'Europe/Dublin': 'IE',
  'Europe/Lisbon': 'PT',
  'Europe/Paris': 'FR',
  'Europe/Madrid': 'ES',
  'Europe/Amsterdam': 'NL',
  'Europe/Brussels': 'BE',
  'Europe/Berlin': 'DE',
  'Europe/Zurich': 'CH',
  'Europe/Vienna': 'AT',
  'Europe/Rome': 'IT',
  'Europe/Prague': 'CZ',
  'Europe/Warsaw': 'PL',
  'Europe/Copenhagen': 'DK',
  'Europe/Stockholm': 'SE',
  'Europe/Oslo': 'NO',
  'Europe/Helsinki': 'FI',
  'Europe/Athens': 'GR',
  'Europe/Moscow': 'RU',
  'Asia/Dubai': 'AE',
  'Asia/Kolkata': 'IN',
  'Asia/Shanghai': 'CN',
  'Asia/Tokyo': 'JP',
  'Asia/Singapore': 'SG',
  'Asia/Seoul': 'KR',
  'Australia/Sydney': 'AU',
  'Australia/Melbourne': 'AU',
  'Pacific/Auckland': 'NZ',
  'America/Sao_Paulo': 'BR',
  'America/Mexico_City': 'MX',
  'America/Toronto': 'CA',
  'Africa/Cairo': 'EG',
  'Africa/Johannesburg': 'ZA',
};

const CACHE_KEY = 'public_holidays_cache_v1';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const loadCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const saveCache = (cache) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage may be full; ignore
  }
};

const fetchYearHolidays = async (countryCode, year) => {
  try {
    const resp = await fetch(
      `https://date.nager.at/api/v3/PublicHolidays/${year}/${countryCode}`
    );
    if (!resp.ok) return [];
    return await resp.json();
  } catch {
    return [];
  }
};

// Fetches holidays for all unique countries in parallel, then commits cache once
// Returns { US: [...holidays], GB: [...], ... } for the current year (plus next year if Dec)
export const fetchAllHolidays = async (timezones) => {
  const year = new Date().getFullYear();
  const nearYearEnd = new Date().getMonth() === 11;
  const countryCodes = [...new Set(timezones.map((tz) => TIMEZONE_TO_COUNTRY[tz]).filter(Boolean))];

  const cache = loadCache();
  const results = {};
  const toFetch = [];

  countryCodes.forEach((cc) => {
    const key = `${cc}_${year}`;
    const cached = cache[key];
    if (cached && cached.expiresAt > Date.now()) {
      results[cc] = cached.data;
    } else {
      toFetch.push(cc);
    }
  });

  // Fetch all required countries in parallel — only write to cache after all finish
  const fetched = await Promise.all(
    toFetch.map(async (cc) => {
      const data = await fetchYearHolidays(cc, year);
      let combined = data;
      if (nearYearEnd) {
        const next = await fetchYearHolidays(cc, year + 1);
        combined = [...data, ...next];
      }
      return { cc, data: combined };
    })
  );

  fetched.forEach(({ cc, data }) => {
    results[cc] = data;
    cache[`${cc}_${year}`] = { data, expiresAt: Date.now() + CACHE_TTL_MS };
  });

  if (fetched.length > 0) saveCache(cache);
  return results;
};

// Returns holiday object if `date` falls on a public holiday in given timezone, else null
// `date` is a JS Date; we evaluate its local date within the timezone.
export const getHolidayForDate = (date, timezone, holidaysByCountry) => {
  if (!holidaysByCountry || !timezone) return null;
  const country = TIMEZONE_TO_COUNTRY[timezone];
  if (!country) return null;
  const list = holidaysByCountry[country];
  if (!list || list.length === 0) return null;

  // Format date in the target timezone as yyyy-MM-dd
  const dateInTz = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);

  return list.find((h) => h.date === dateInTz) || null;
};
